<template>
<div>
  <span class="d-flex mx-4 my-4">
    <v-btn
      color="primary"
      class="white--text mt-1"
      @click="navigateBack()">
      <v-icon large>mdi-chevron-left</v-icon>
    </v-btn>
    <v-spacer />
    <div class="mt-2 text-h5 text-center">{{ docName }}</div>
    <v-spacer />
  </span>

  <div v-if="pdfError" class="mx-4 error--text">{{ pdfError }}</div>
  <div v-else class="pdf-viewer mx-4 mb-4">
    <div class="pdf-controls px-3 py-2">
      <v-btn small class="mr-2" :disabled="isLoading" @click="zoomOut">
        <v-icon small>mdi-magnify-minus-outline</v-icon>
      </v-btn>
      <v-btn small class="mr-2" :disabled="isLoading" @click="zoomIn">
        <v-icon small>mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <v-btn small class="mr-2" :disabled="isLoading" @click="fitToWidth" title="Fit to width">
        <v-icon small>mdi-fit-to-page-outline</v-icon>
      </v-btn>
      <v-btn small :disabled="isLoading" @click="resetZoom" title="Reset zoom to 100%">
        <v-icon small>mdi-crop-free</v-icon>
      </v-btn>
      <v-spacer />
      <span class="text-caption font-weight-medium">{{ Math.round(zoomScale * 100) }}%</span>
    </div>

    <div v-if="isLoading" class="text-center py-6">Loading document...</div>
    
    <div
      ref="pdfContainer"
      :class="['pdf-container', { 'pdf-container--fit-width': fitMode === 'width' }]"
    />
  </div>
</div>
</template>

<script>
import installationPdf from "@/assets/docs/Installing-Application.pdf";
import releaseNotesPdf from "@/assets/docs/Release-Notes.pdf";
import visionSopPdf from "@/assets/docs/Vision-Standard-Operating-Procedure.pdf";

import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.js`;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const FIT_WIDTH_BUFFER = 6;

export default {
  data() {
    return {
      docName: null,
      pdfSrc: null,
      isLoading: false,
      pdfError: null,
      pdfDocument: null,
      zoomScale: 1,
      fitMode: "width"
    };
  },
  created() {
    this.docName = this.$route.params.docName;
    this.pdfSrc = this.resolvePdfSource(this.docName);
  },
  async mounted() {
    await this.loadPdf();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    resolvePdfSource(name) {
      switch (name) {
        case "Installation Instructions":
          return installationPdf;
        case "Release Notes":
          return releaseNotesPdf;
        case "Vision SOP":
          return visionSopPdf;
        default:
          return null;
      }
    },
    async loadPdf() {
      if (!this.pdfSrc) {
        this.pdfError = "Document not found.";
        return;
      }

      this.isLoading = true;
      this.pdfError = null;

      try {
        this.pdfDocument = await pdfjsLib.getDocument({
          url: this.pdfSrc,
          useSystemFonts: true,
          disableFontFace: true
        }).promise;
        await this.renderPdf();
      } catch (error) {
        console.error("PDF load failed", error);
        this.pdfError = "Unable to load this PDF on your device.";
      } finally {
        this.isLoading = false;
      }
    },
    async renderPdf() {
      if (!this.pdfDocument) {
        return;
      }

      const container = this.$refs.pdfContainer;
      if (!container) {
        this.pdfError = "Could not initialize PDF viewer.";
        return;
      }

      this.isLoading = true;
      container.innerHTML = "";

      try {
        const containerWidth = this.getContainerContentWidth(container);
        const devicePixelRatio = window.devicePixelRatio || 1;

        for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
          const page = await this.pdfDocument.getPage(pageNumber);
          const baseViewport = page.getViewport({ scale: 1 });
          const fitWidthScale = this.getFitWidthScale(containerWidth, baseViewport.width);
          const scale = this.fitMode === "width" ? fitWidthScale : this.zoomScale;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.className = "pdf-page";
          const context = canvas.getContext("2d");
          if (!context) {
            continue;
          }

          canvas.width = Math.floor(viewport.width * devicePixelRatio);
          canvas.height = Math.floor(viewport.height * devicePixelRatio);
          if (this.fitMode === "width") {
            canvas.style.width = "100%";
            canvas.style.maxWidth = `${Math.floor(viewport.width)}px`;
            canvas.style.height = "auto";
          } else {
            canvas.style.width = `${Math.floor(viewport.width)}px`;
            canvas.style.height = `${Math.floor(viewport.height)}px`;
            canvas.style.maxWidth = "none";
          }

          context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
          container.appendChild(canvas);

          await page.render({
            canvasContext: context,
            viewport
          }).promise;
        }

        if (this.fitMode === "width") {
          const firstPage = await this.pdfDocument.getPage(1);
          const firstPageViewport = firstPage.getViewport({ scale: 1 });
          this.zoomScale = this.getFitWidthScale(containerWidth, firstPageViewport.width);
        }
      } catch (error) {
        console.error("PDF render failed", error);
        this.pdfError = "Unable to load this PDF on your device.";
      } finally {
        this.isLoading = false;
      }
    },
    clampZoom(value) {
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
    },
    getContainerContentWidth(container) {
      const styles = window.getComputedStyle(container);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const availableWidth = container.clientWidth - paddingLeft - paddingRight;
      return Math.max(availableWidth, 120);
    },
    getFitWidthScale(containerWidth, pageWidth) {
      // A small buffer to prevent horizontal scrollbars is used due to rounding 
      // issues and minor layout differences.  Also enforce a minimum width of 120px
      // to avoid extreme zoom levels on very narrow containers.
      const safeWidth = Math.max(containerWidth - FIT_WIDTH_BUFFER, 120);
      return safeWidth / pageWidth;
    },
    async zoomIn() {
      this.fitMode = "manual";
      this.zoomScale = this.clampZoom(this.zoomScale + ZOOM_STEP);
      await this.renderPdf();
    },
    async zoomOut() {
      this.fitMode = "manual";
      this.zoomScale = this.clampZoom(this.zoomScale - ZOOM_STEP);
      await this.renderPdf();
    },
    async fitToWidth() {
      this.fitMode = "width";
      await this.renderPdf();
    },
    async resetZoom() {
      this.fitMode = "manual";
      this.zoomScale = 1;
      await this.renderPdf();
    },
    async handleResize() {
      if (this.fitMode === "width" && this.pdfDocument && !this.isLoading) {
        await this.renderPdf();
      }
    },
    navigateBack() {
       this.$router.go(-1);
    }
  }
}
</script>
<style>
.pdf-viewer {
  height: 80vh;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
}

.pdf-controls {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d9d9d9;
  background: #ffffff;
}

.pdf-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  overflow-y: auto;
  overflow-x: auto;
  height: calc(80vh - 52px);
  box-sizing: border-box;
  min-height: calc(80vh - 52px);
}

.pdf-container--fit-width {
  align-items: center;
  overflow-x: hidden;
}

.pdf-page {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  background: #fff;
}
</style>